import type {
  PlanogramTemplate,
  MilestoneConfig,
  CycleConfig,
  ScopeConfig,
  UsageStatistics,
  PerformanceMetrics,
  PlanType,
  TemplateStatus,
  ResponsibleTeam,
  CycleFrequencyType,
  TerminationType,
  ScopeType,
  WizardStep,
  CycleStatus
} from '../types/plan-template';
import { 
  generateFirstExecutionTimestamp, 
  generateMockTimestamp, 
  getTomorrowDateString,
  dateStringToTimestamp,
  getCurrentTimestamp
} from '../utils/dateTimeUtil';

// Re-export types for external use
export type {
  PlanogramTemplate,
  MilestoneConfig,
  CycleConfig,
  ScopeConfig,
  UsageStatistics,
  PerformanceMetrics,
  PlanType,
  TemplateStatus,
  ResponsibleTeam,
  CycleFrequencyType,
  TerminationType,
  ScopeType,
  WizardStep,
  CycleStatus
};

// ==================== CONSTANTS SECTION ====================

// Plan type constants
export const PLAN_TYPES: { value: PlanType; label: string; description: string }[] = [
  {
    value: 'Major Plans',
    label: 'Major Plans',
    description: 'Large-scale product adjustments involving multiple categories and large quantities of products re-layout'
  },
  {
    value: 'Regular Plans',
    label: 'Regular Plans',
    description: 'Regular product adjustments to maintain display freshness and rationality'
  },
  {
    value: 'Minor Plans',
    label: 'Minor Plans',
    description: 'Local product adjustments targeting specific areas or category optimization'
  },
  {
    value: 'Urgent Plans',
    label: 'Urgent Plans',
    description: 'Emergency product adjustments, rapid response to market changes or emergencies'
  },
  {
    value: 'Special Plans',
    label: 'Special Plans',
    description: 'Product adjustments for specific purposes such as holiday promotions, new product launches, etc.'
  }
];

// Template status constants
export const TEMPLATE_STATUSES: { value: TemplateStatus; label: string; color: string }[] = [
  { value: 'Draft', label: 'Draft', color: 'default' },
  { value: 'Published', label: 'Published', color: 'success' },
  { value: 'Disabled', label: 'Disabled', color: 'error' }
];

// Responsible team constants
export const RESPONSIBLE_TEAMS: { value: ResponsibleTeam; label: string }[] = [
  { value: 'Planogram Team', label: 'Planogram Team' },
  { value: 'Construction Team', label: 'Construction Team' },
  { value: 'Floor Plan Team', label: 'Floor Plan Team' },
  { value: 'Procurement Team', label: 'Procurement Team' },
  { value: 'Marketing Team', label: 'Marketing Team' },
  { value: 'Training Team', label: 'Training Team' },
  { value: 'Visual Team', label: 'Visual Team' },
  { value: 'System Auto', label: 'System Auto' }
];

// Cycle frequency type constants
export const CYCLE_FREQUENCY_TYPES: { value: CycleFrequencyType; label: string }[] = [
  { value: 'Daily', label: 'Daily' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Yearly', label: 'Yearly' }
];

// Termination type constants
export const TERMINATION_TYPES: { value: TerminationType; label: string }[] = [
  { value: 'Infinite Repeat', label: 'Infinite Repeat' },
  { value: 'End on Date', label: 'End on Date' },
  { value: 'Limited Count', label: 'Limited Count' }
];

// Scope type constants
export const SCOPE_TYPES: { value: ScopeType; label: string; description: string }[] = [
  {
    value: 'All Stores',
    label: 'All Stores',
    description: 'Applied to all stores'
  },
  {
    value: 'Specific Store Groups',
    label: 'Specific Store Groups',
    description: 'Applied to selected store groups'
  },
  {
    value: 'Custom Store Collections',
    label: 'Custom Store Collections',
    description: 'Filter stores based on criteria'
  },
  {
    value: 'Individual Stores',
    label: 'Individual Stores',
    description: 'Manually select specific stores'
  }
];

// Wizard step constants
export const WIZARD_STEPS: { key: WizardStep; title: string; description: string }[] = [
  {
    key: 'planType',
    title: 'Select Plan Type',
    description: 'Select appropriate plan type to determine default configuration'
  },
  {
    key: 'basicInfo',
    title: 'Basic Information',
    description: 'Fill in template basic information and description'
  },
  {
    key: 'milestones',
    title: 'Milestone Configuration',
    description: 'Configure key milestones in the execution process'
  },
  {
    key: 'cycle',
    title: 'Execution Cycle',
    description: 'Set template execution frequency and cycle'
  },
  {
    key: 'scope',
    title: 'Impact Scope',
    description: 'Define store scope for template application'
  },
  {
    key: 'validation',
    title: 'Validation Preview',
    description: 'Validate configuration and preview generated plans'
  }
];

// Lifecycle status constants
export const LIFECYCLE_STATUSES: { value: CycleStatus; label: string; color: string }[] = [
  { value: 'Active', label: 'Active', color: 'success' },
  { value: 'Paused', label: 'Paused', color: 'warning' },
  { value: 'Terminated', label: 'Terminated', color: 'error' }
];

// Business activities
export const BUSINESS_ACTIVITIES = [
  'Quarterly Adjustment',
  'Monthly Adjustment',
  'Bi-weekly Adjustment',
  'New Product Launch',
  'Promotional Activity',
  'Festival Display',
  'Inventory Clearance',
  'Category Optimization',
  'Space Restructuring',
  'Brand Upgrade'
];

// UI configuration constants
export const UI_CONFIG = {
  // Table pagination
  TABLE_PAGE_SIZE_OPTIONS: ['10', '20', '50', '100'],
  DEFAULT_PAGE_SIZE: 20,
  
  // Form validation
  DEBOUNCE_DELAY: 300,
  
  // Template code format
  TEMPLATE_CODE_PREFIX: 'TPL',
  TEMPLATE_CODE_LENGTH: 8,
  
  // Date formats
  DATE_FORMAT: 'YYYY-MM-DD',
  DATETIME_FORMAT: 'YYYY-MM-DD HH:mm:ss',
  
  // File upload
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
  ALLOWED_FILE_TYPES: ['.xlsx', '.xls', '.csv'],
  
  // Milestone configuration
  MAX_MILESTONES: 9,
  MIN_MILESTONES: 3,
  MAX_DURATION_DAYS: 365,
  MAX_BUFFER_DAYS: 30,
  
  // Cycle configuration
  MAX_INTERVAL: 999,
  MAX_REPEAT_COUNT: 9999,
  
  // Scope configuration
  MAX_SELECTED_STORES: 10000,
  
  // Colors
  COLORS: {
    PRIMARY: '#1890ff',
    SUCCESS: '#52c41a',
    WARNING: '#faad14',
    ERROR: '#f5222d',
    INFO: '#13c2c2'
  },
  
  // Animation durations
  ANIMATION: {
    FAST: 200,
    NORMAL: 300,
    SLOW: 500
  }
};

// Error messages
export const ERROR_MESSAGES = {
  REQUIRED_FIELD: 'This field is required',
  INVALID_FORMAT: 'Invalid format',
  CODE_EXISTS: 'Template code already exists',
  NETWORK_ERROR: 'Network connection failed, please try again',
  VALIDATION_FAILED: 'Validation failed, please check input',
  OPERATION_FAILED: 'Operation failed, please try again',
  UNAUTHORIZED: 'No permission to perform this operation',
  SERVER_ERROR: 'Server error, please contact administrator',
  
  // Template specific
  TEMPLATE_NOT_FOUND: 'Template not found',
  TEMPLATE_IN_USE: 'Template is in use, cannot delete',
  INVALID_MILESTONE_SEQUENCE: 'Invalid milestone sequence',
  MISSING_FINAL_MILESTONE: 'Missing final milestone "Store Task Generation"',
  INVALID_CYCLE_CONFIG: 'Invalid execution cycle configuration',
  INVALID_SCOPE_CONFIG: 'Invalid impact scope configuration',
  
  // Wizard specific
  STEP_VALIDATION_FAILED: 'Current step validation failed',
  UNSAVED_CHANGES: 'You have unsaved changes, are you sure you want to leave?'
};

// Success messages
export const SUCCESS_MESSAGES = {
  TEMPLATE_CREATED: 'Template created successfully',
  TEMPLATE_UPDATED: 'Template updated successfully',
  TEMPLATE_DELETED: 'Template deleted successfully',
  BATCH_OPERATION_SUCCESS: 'Batch operation completed',
  VALIDATION_PASSED: 'Validation passed'
};

// Loading messages
export const LOADING_MESSAGES = {
  LOADING_TEMPLATES: 'Loading template list...',
  LOADING_TEMPLATE_DETAIL: 'Loading template details...',
  CREATING_TEMPLATE: 'Creating template...',
  UPDATING_TEMPLATE: 'Updating template...',
  DELETING_TEMPLATE: 'Deleting template...',
  VALIDATING_TEMPLATE: 'Validating template...',
  LOADING_REFERENCE_DATA: 'Loading reference data...'
};

// API endpoints
export const API_ENDPOINTS = {
  TEMPLATES: {
    LIST: '/api/v1/planogram-templates/list',
    DETAIL: '/api/v1/planogram-templates/detail',
    CREATE: '/api/v1/planogram-templates/create',
    UPDATE: '/api/v1/planogram-templates/update',
    DELETE: '/api/v1/planogram-templates/delete',
    BATCH_UPDATE_STATUS: '/api/v1/planogram-templates/batch-update-status',
    VALIDATE: '/api/v1/planogram-templates/validate',
    PREVIEW_PLANS: '/api/v1/planogram-templates/preview-plans',
    CHECK_CODE_UNIQUENESS: '/api/v1/planogram-templates/check-code-uniqueness'
  },
  REFERENCE_DATA: {
    MILESTONE_TYPES: '/api/v1/planogram-templates/milestone-types',
    STORE_GROUPS: '/api/v1/planogram-templates/store-groups',
    STORES: '/api/v1/planogram-templates/stores',
    BUSINESS_ACTIVITIES: '/api/v1/planogram-templates/business-activities'
  }
};

// Local storage keys
export const STORAGE_KEYS = {
  WIZARD_STATE: 'planogram_template_wizard_state',
  USER_PREFERENCES: 'planogram_template_user_preferences',
  FILTER_STATE: 'planogram_template_filter_state'
};

// Regular expressions
export const REGEX_PATTERNS = {
  TEMPLATE_CODE: /^[A-Z0-9]{3,20}$/,
  TEMPLATE_NAME: /^[\u4e00-\u9fa5a-zA-Z0-9\s]{2,50}$/,
  POSITIVE_INTEGER: /^[1-9]\d*$/,
  POSITIVE_NUMBER: /^[1-9]\d*(\.\d+)?$/
};

// Status transition rules
export const STATUS_TRANSITIONS: Record<TemplateStatus, TemplateStatus[]> = {
  'Draft': ['Published'],
  'Published': ['Disabled'],
  'Disabled': ['Published']
};

// Audit action types
export const AUDIT_ACTIONS = {
  CREATE: 'Create',
  UPDATE: 'Update',
  DELETE: 'Delete',
  STATUS_CHANGE: 'StatusChange',
  PUBLISH: 'Publish',
  UNPUBLISH: 'Unpublish'
} as const;

// ==================== MOCK DATA GENERATION SECTION ====================

// ==================== UTILITY FUNCTIONS ====================

/**
 * Generate unique template ID with timestamp and random string
 */
const generateUniqueId = (): string => {
  return `template_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Generate template code with prefix, timestamp and random string
 */
const generateTemplateCode = (): string => {
  const timestamp = Date.now().toString().slice(-6);
  const randomStr = Math.random().toString(36).substr(2, 4).toUpperCase();
  return `${UI_CONFIG.TEMPLATE_CODE_PREFIX}${timestamp}${randomStr}`;
};

/**
 * Get random item from array
 */
const getRandomItem = <T>(array: T[]): T => {
  return array[Math.floor(Math.random() * array.length)];
};

/**
 * Get random date within specified range
 */
const getRandomDate = (start: Date, end: Date): Date => {
  return new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
};

// Default milestone configurations by plan type
const DEFAULT_MILESTONES_BY_PLAN_TYPE: Record<PlanType, Partial<MilestoneConfig>[]> = {
  'Major Plans': [
    // Major Plans: Complete 9 milestones
    {
      code: 'DISPLAY_GROUP_ADJUSTMENT',
      name: 'Display Group Adjustment',
      description: 'Adjust Sub-Class associations of HQ Display Group, affecting product grouping logic',
      sequence: 1,
      responsibleTeam: 'Planogram Team',
      durationEstimate: 5,
      bufferTime: 2,
      parallelExecution: false,
      isFinalMilestone: false,
      prerequisites: [],
      deliverables: ['Updated Display Group configuration']
    },
    {
      code: 'STORE_GROUP_SETUP',
      name: 'Store Group Setup',
      description: 'Adjust store scope under Store Group, determining stores covered by the plan',
      sequence: 2,
      responsibleTeam: 'Planogram Team',
      durationEstimate: 3,
      bufferTime: 1,
      parallelExecution: false,
      isFinalMilestone: false,
      prerequisites: ['DISPLAY_GROUP_ADJUSTMENT'],
      deliverables: ['Determined target store list']
    },
    {
      code: 'BENCHMARK_MASTER_DRAWING',
      name: 'Benchmark Master Drawing',
      description: 'Draw Benchmark drawings in Nielsen software based on Store Group',
      sequence: 3,
      responsibleTeam: 'Planogram Team',
      durationEstimate: 7,
      bufferTime: 3,
      parallelExecution: false,
      isFinalMilestone: false,
      prerequisites: ['STORE_GROUP_SETUP'],
      deliverables: ['Benchmark display diagram']
    },
    {
      code: 'STORE_LAYOUT_ADJUSTMENT',
      name: 'Store Layout Adjustment',
      description: 'Execute physical layout adjustments in stores',
      sequence: 4,
      responsibleTeam: 'Construction Team',
      durationEstimate: 10,
      bufferTime: 3,
      parallelExecution: true,
      isFinalMilestone: false,
      prerequisites: ['BENCHMARK_MASTER_DRAWING'],
      deliverables: ['Updated store layout']
    },
    {
      code: 'FIXTURE_TYPE_SETUP',
      name: 'Fixture Type Set Up',
      description: 'Set up new fixture types in master data for use in display planning',
      sequence: 5,
      responsibleTeam: 'Floor Plan Team',
      durationEstimate: 5,
      bufferTime: 2,
      parallelExecution: true,
      isFinalMilestone: false,
      prerequisites: ['BENCHMARK_MASTER_DRAWING'],
      deliverables: ['New fixture type configuration']
    },
    {
      code: 'DISPLAY_PLANNING_ADJUSTMENT',
      name: 'Display Planning Adjustment',
      description: 'Adjust section numbering and Display Group associations',
      sequence: 6,
      responsibleTeam: 'Floor Plan Team',
      durationEstimate: 6,
      bufferTime: 2,
      parallelExecution: false,
      isFinalMilestone: false,
      prerequisites: ['FIXTURE_TYPE_SETUP', 'STORE_LAYOUT_ADJUSTMENT'],
      deliverables: ['Updated display planning']
    },
    {
      code: 'FIXTURE_PROCUREMENT',
      name: 'Fixture Procurement',
      description: 'Generate fixture procurement requirements based on layout and display adjustments',
      sequence: 7,
      responsibleTeam: 'Procurement Team',
      durationEstimate: 8,
      bufferTime: 3,
      parallelExecution: false,
      isFinalMilestone: false,
      prerequisites: ['DISPLAY_PLANNING_ADJUSTMENT'],
      deliverables: ['Purchase orders']
    },
    {
      code: 'DISPLAY_DIAGRAM_GENERATION',
      name: 'Algorithm Generated Planogram for Review',
      description: 'Generate store display diagrams at specified times and conduct review confirmation',
      sequence: 8,
      responsibleTeam: 'System Auto',
      durationEstimate: 4,
      bufferTime: 2,
      parallelExecution: false,
      isFinalMilestone: false,
      prerequisites: ['FIXTURE_PROCUREMENT'],
      deliverables: ['Final display diagram']
    },
    {
      code: 'STORE_TASK_GENERATION',
      name: 'Store Task Generation',
      description: 'Generate final store display adjustment tasks and distribute for execution',
      sequence: 9,
      responsibleTeam: 'System Auto',
      durationEstimate: 1,
      bufferTime: 0,
      parallelExecution: false,
      isFinalMilestone: true,
      prerequisites: ['DISPLAY_DIAGRAM_GENERATION'],
      deliverables: ['Store execution tasks']
    }
  ],
  'Regular Plans': [
    // Regular Plans: 6 milestones
    {
      code: 'BENCHMARK_MASTER_DRAWING',
      name: 'Benchmark Master Drawing',
      description: 'Draw Benchmark drawings in Nielsen software based on Store Group',
      sequence: 1,
      responsibleTeam: 'Planogram Team',
      durationEstimate: 4,
      bufferTime: 2,
      parallelExecution: false,
      isFinalMilestone: false,
      prerequisites: [],
      deliverables: ['Benchmark display diagram']
    },
    {
      code: 'STORE_LAYOUT_ADJUSTMENT',
      name: 'Store Layout Adjustment',
      description: 'Execute physical layout adjustments in stores',
      sequence: 2,
      responsibleTeam: 'Construction Team',
      durationEstimate: 6,
      bufferTime: 2,
      parallelExecution: false,
      isFinalMilestone: false,
      prerequisites: ['BENCHMARK_MASTER_DRAWING'],
      deliverables: ['Updated store layout']
    },
    {
      code: 'DISPLAY_PLANNING_ADJUSTMENT',
      name: 'Display Planning Adjustment',
      description: 'Adjust section numbering and Display Group associations',
      sequence: 3,
      responsibleTeam: 'Floor Plan Team',
      durationEstimate: 4,
      bufferTime: 1,
      parallelExecution: false,
      isFinalMilestone: false,
      prerequisites: ['STORE_LAYOUT_ADJUSTMENT'],
      deliverables: ['Updated display planning']
    },
    {
      code: 'FIXTURE_PROCUREMENT',
      name: 'Fixture Procurement',
      description: 'Generate fixture procurement requirements based on layout and display adjustments',
      sequence: 4,
      responsibleTeam: 'Procurement Team',
      durationEstimate: 5,
      bufferTime: 2,
      parallelExecution: false,
      isFinalMilestone: false,
      prerequisites: ['DISPLAY_PLANNING_ADJUSTMENT'],
      deliverables: ['Purchase orders']
    },
    {
      code: 'DISPLAY_DIAGRAM_GENERATION',
      name: 'Algorithm Generated Planogram for Review',
      description: 'Generate store display diagrams at specified times and conduct review confirmation',
      sequence: 5,
      responsibleTeam: 'System Auto',
      durationEstimate: 3,
      bufferTime: 1,
      parallelExecution: false,
      isFinalMilestone: false,
      prerequisites: ['FIXTURE_PROCUREMENT'],
      deliverables: ['Final display diagram']
    },
    {
      code: 'STORE_TASK_GENERATION',
      name: 'Store Task Generation',
      description: 'Generate final store display adjustment tasks and distribute for execution',
      sequence: 6,
      responsibleTeam: 'System Auto',
      durationEstimate: 1,
      bufferTime: 0,
      parallelExecution: false,
      isFinalMilestone: true,
      prerequisites: ['DISPLAY_DIAGRAM_GENERATION'],
      deliverables: ['Store execution tasks']
    }
  ],
  'Minor Plans': [
    // Minor Plans: 4 milestones
    {
      code: 'DISPLAY_PLANNING_ADJUSTMENT',
      name: 'Display Planning Adjustment',
      description: 'Adjust section numbering and Display Group associations',
      sequence: 1,
      responsibleTeam: 'Floor Plan Team',
      durationEstimate: 2,
      bufferTime: 1,
      parallelExecution: false,
      isFinalMilestone: false,
      prerequisites: [],
      deliverables: ['Updated display planning']
    },
    {
      code: 'FIXTURE_PROCUREMENT',
      name: 'Fixture Procurement',
      description: 'Generate fixture procurement requirements based on layout and display adjustments',
      sequence: 2,
      responsibleTeam: 'Procurement Team',
      durationEstimate: 3,
      bufferTime: 1,
      parallelExecution: false,
      isFinalMilestone: false,
      prerequisites: ['DISPLAY_PLANNING_ADJUSTMENT'],
      deliverables: ['Purchase orders']
    },
    {
      code: 'DISPLAY_DIAGRAM_GENERATION',
      name: 'Algorithm Generated Planogram for Review',
      description: 'Generate store display diagrams at specified times and conduct review confirmation',
      sequence: 3,
      responsibleTeam: 'System Auto',
      durationEstimate: 2,
      bufferTime: 1,
      parallelExecution: false,
      isFinalMilestone: false,
      prerequisites: ['FIXTURE_PROCUREMENT'],
      deliverables: ['Final display diagram']
    },
    {
      code: 'STORE_TASK_GENERATION',
      name: 'Store Task Generation',
      description: 'Generate final store display adjustment tasks and distribute for execution',
      sequence: 4,
      responsibleTeam: 'System Auto',
      durationEstimate: 1,
      bufferTime: 0,
      parallelExecution: false,
      isFinalMilestone: true,
      prerequisites: ['DISPLAY_DIAGRAM_GENERATION'],
      deliverables: ['Store execution tasks']
    }
  ],
  'Urgent Plans': [
    // Urgent Plans: Complete 9 milestones, but compressed time
    {
      code: 'DISPLAY_GROUP_ADJUSTMENT',
      name: 'Display Group Adjustment',
      description: 'Adjust Sub-Class associations of HQ Display Group, affecting product grouping logic',
      sequence: 1,
      responsibleTeam: 'Planogram Team',
      durationEstimate: 1,
      bufferTime: 0,
      parallelExecution: false,
      isFinalMilestone: false,
      prerequisites: [],
      deliverables: ['Updated Display Group configuration']
    },
    {
      code: 'STORE_GROUP_SETUP',
      name: 'Store Group Setup',
      description: 'Adjust store scope under Store Group, determining stores covered by the plan',
      sequence: 2,
      responsibleTeam: 'Planogram Team',
      durationEstimate: 1,
      bufferTime: 0,
      parallelExecution: false,
      isFinalMilestone: false,
      prerequisites: ['DISPLAY_GROUP_ADJUSTMENT'],
      deliverables: ['Determined target store list']
    },
    {
      code: 'BENCHMARK_MASTER_DRAWING',
      name: 'Benchmark Master Drawing',
      description: 'Draw Benchmark drawings in Nielsen software based on Store Group',
      sequence: 3,
      responsibleTeam: 'Planogram Team',
      durationEstimate: 2,
      bufferTime: 1,
      parallelExecution: false,
      isFinalMilestone: false,
      prerequisites: ['STORE_GROUP_SETUP'],
      deliverables: ['Benchmark display diagram']
    },
    {
      code: 'STORE_LAYOUT_ADJUSTMENT',
      name: 'Store Layout Adjustment',
      description: 'Execute physical layout adjustments in stores',
      sequence: 4,
      responsibleTeam: 'Construction Team',
      durationEstimate: 3,
      bufferTime: 1,
      parallelExecution: true,
      isFinalMilestone: false,
      prerequisites: ['BENCHMARK_MASTER_DRAWING'],
      deliverables: ['Updated store layout']
    },
    {
      code: 'FIXTURE_TYPE_SETUP',
      name: 'Fixture Type Set Up',
      description: 'Set up new fixture types in master data for use in display planning',
      sequence: 5,
      responsibleTeam: 'Floor Plan Team',
      durationEstimate: 1,
      bufferTime: 0,
      parallelExecution: true,
      isFinalMilestone: false,
      prerequisites: ['BENCHMARK_MASTER_DRAWING'],
      deliverables: ['New fixture type configuration']
    },
    {
      code: 'DISPLAY_PLANNING_ADJUSTMENT',
      name: 'Display Planning Adjustment',
      description: 'Adjust section numbering and Display Group associations',
      sequence: 6,
      responsibleTeam: 'Floor Plan Team',
      durationEstimate: 1,
      bufferTime: 0,
      parallelExecution: false,
      isFinalMilestone: false,
      prerequisites: ['FIXTURE_TYPE_SETUP', 'STORE_LAYOUT_ADJUSTMENT'],
      deliverables: ['Updated display planning']
    },
    {
      code: 'FIXTURE_PROCUREMENT',
      name: 'Fixture Procurement',
      description: 'Generate fixture procurement requirements based on layout and display adjustments',
      sequence: 7,
      responsibleTeam: 'Procurement Team',
      durationEstimate: 2,
      bufferTime: 1,
      parallelExecution: false,
      isFinalMilestone: false,
      prerequisites: ['DISPLAY_PLANNING_ADJUSTMENT'],
      deliverables: ['Purchase orders']
    },
    {
      code: 'DISPLAY_DIAGRAM_GENERATION',
      name: 'Algorithm Generated Planogram for Review',
      description: 'Generate store display diagrams at specified times and conduct review confirmation',
      sequence: 8,
      responsibleTeam: 'System Auto',
      durationEstimate: 1,
      bufferTime: 0,
      parallelExecution: false,
      isFinalMilestone: false,
      prerequisites: ['FIXTURE_PROCUREMENT'],
      deliverables: ['Final display diagram']
    },
    {
      code: 'STORE_TASK_GENERATION',
      name: 'Store Task Generation',
      description: 'Generate final store display adjustment tasks and distribute for execution',
      sequence: 9,
      responsibleTeam: 'System Auto',
      durationEstimate: 1,
      bufferTime: 0,
      parallelExecution: false,
      isFinalMilestone: true,
      prerequisites: ['DISPLAY_DIAGRAM_GENERATION'],
      deliverables: ['Store execution tasks']
    }
  ],
  'Special Plans': [
    // Special Plans: Complete 9 milestones
    {
      code: 'DISPLAY_GROUP_ADJUSTMENT',
      name: 'Display Group Adjustment',
      description: 'Adjust Sub-Class associations of HQ Display Group, affecting product grouping logic',
      sequence: 1,
      responsibleTeam: 'Planogram Team',
      durationEstimate: 4,
      bufferTime: 2,
      parallelExecution: false,
      isFinalMilestone: false,
      prerequisites: [],
      deliverables: ['Updated Display Group configuration']
    },
    {
      code: 'STORE_GROUP_SETUP',
      name: 'Store Group Setup',
      description: 'Adjust store scope under Store Group, determining stores covered by the plan',
      sequence: 2,
      responsibleTeam: 'Planogram Team',
      durationEstimate: 2,
      bufferTime: 1,
      parallelExecution: false,
      isFinalMilestone: false,
      prerequisites: ['DISPLAY_GROUP_ADJUSTMENT'],
      deliverables: ['Determined target store list']
    },
    {
      code: 'BENCHMARK_MASTER_DRAWING',
      name: 'Benchmark Master Drawing',
      description: 'Draw Benchmark drawings in Nielsen software based on Store Group',
      sequence: 3,
      responsibleTeam: 'Planogram Team',
      durationEstimate: 6,
      bufferTime: 2,
      parallelExecution: false,
      isFinalMilestone: false,
      prerequisites: ['STORE_GROUP_SETUP'],
      deliverables: ['Benchmark display diagram']
    },
    {
      code: 'STORE_LAYOUT_ADJUSTMENT',
      name: 'Store Layout Adjustment',
      description: 'Execute physical layout adjustments in stores',
      sequence: 4,
      responsibleTeam: 'Construction Team',
      durationEstimate: 8,
      bufferTime: 3,
      parallelExecution: true,
      isFinalMilestone: false,
      prerequisites: ['BENCHMARK_MASTER_DRAWING'],
      deliverables: ['Updated store layout']
    },
    {
      code: 'FIXTURE_TYPE_SETUP',
      name: 'Fixture Type Set Up',
      description: 'Set up new fixture types in master data for use in display planning',
      sequence: 5,
      responsibleTeam: 'Floor Plan Team',
      durationEstimate: 4,
      bufferTime: 2,
      parallelExecution: true,
      isFinalMilestone: false,
      prerequisites: ['BENCHMARK_MASTER_DRAWING'],
      deliverables: ['New fixture type configuration']
    },
    {
      code: 'DISPLAY_PLANNING_ADJUSTMENT',
      name: 'Display Planning Adjustment',
      description: 'Adjust section numbering and Display Group associations',
      sequence: 6,
      responsibleTeam: 'Floor Plan Team',
      durationEstimate: 5,
      bufferTime: 2,
      parallelExecution: false,
      isFinalMilestone: false,
      prerequisites: ['FIXTURE_TYPE_SETUP', 'STORE_LAYOUT_ADJUSTMENT'],
      deliverables: ['Updated display planning']
    },
    {
      code: 'FIXTURE_PROCUREMENT',
      name: 'Fixture Procurement',
      description: 'Generate fixture procurement requirements based on layout and display adjustments',
      sequence: 7,
      responsibleTeam: 'Procurement Team',
      durationEstimate: 7,
      bufferTime: 3,
      parallelExecution: false,
      isFinalMilestone: false,
      prerequisites: ['DISPLAY_PLANNING_ADJUSTMENT'],
      deliverables: ['Purchase orders']
    },
    {
      code: 'DISPLAY_DIAGRAM_GENERATION',
      name: 'Algorithm Generated Planogram for Review',
      description: 'Generate store display diagrams at specified times and conduct review confirmation',
      sequence: 8,
      responsibleTeam: 'System Auto',
      durationEstimate: 3,
      bufferTime: 2,
      parallelExecution: false,
      isFinalMilestone: false,
      prerequisites: ['FIXTURE_PROCUREMENT'],
      deliverables: ['Final display diagram']
    },
    {
      code: 'STORE_TASK_GENERATION',
      name: 'Store Task Generation',
      description: 'Generate final store display adjustment tasks and distribute for execution',
      sequence: 9,
      responsibleTeam: 'System Auto',
      durationEstimate: 1,
      bufferTime: 0,
      parallelExecution: false,
      isFinalMilestone: true,
      prerequisites: ['DISPLAY_DIAGRAM_GENERATION'],
      deliverables: ['Store execution tasks']
    }
  ]
};

// Mock milestone configurations for different plan types
const createMilestoneConfigs = (planType: PlanType): MilestoneConfig[] => {
  const defaultMilestones = DEFAULT_MILESTONES_BY_PLAN_TYPE[planType] || DEFAULT_MILESTONES_BY_PLAN_TYPE['Regular Plans'];
  
  return defaultMilestones.map((milestone) => ({
    id: generateUniqueId(),
    code: milestone.code,
    name: milestone.name,
    description: milestone.description,
    sequence: milestone.sequence,
    responsibleTeam: milestone.responsibleTeam,
    prerequisites: milestone.prerequisites,
    deliverables: milestone.deliverables,
    durationEstimate: milestone.durationEstimate,
    bufferTime: milestone.bufferTime,
    parallelExecution: milestone.parallelExecution,
    isFinalMilestone: milestone.isFinalMilestone
  }));
};

// Create cycle configurations
const createCycleConfig = (planType: PlanType): CycleConfig => {
  const cycleConfigs: Record<PlanType, Partial<CycleConfig>> = {
    'Major Plans': {
      frequencyType: 'Yearly',
      interval: 1,
      firstExecutionDate: generateFirstExecutionTimestamp('2024-03-01'),
      terminationType: 'Infinite Repeat',
      cycleStatus: 'Active'
    },
    'Regular Plans': {
      frequencyType: 'Monthly',
      interval: 1,
      monthDaySelection: [1, 15],
      firstExecutionDate: generateFirstExecutionTimestamp('2024-01-01'),
      terminationType: 'Infinite Repeat',
      cycleStatus: 'Active'
    },
    'Minor Plans': {
      frequencyType: 'Weekly',
      interval: 2,
      weekDaySelection: [1, 3, 5],
      firstExecutionDate: generateFirstExecutionTimestamp('2024-01-08'),
      terminationType: 'Infinite Repeat',
      cycleStatus: 'Active'
    },
    'Urgent Plans': {
      frequencyType: 'Daily',
      interval: 1,
      firstExecutionDate: generateFirstExecutionTimestamp('2024-01-01'),
      terminationType: 'Limited Count',
      repeatCount: 365,
      cycleStatus: 'Active'
    },
    'Special Plans': {
      frequencyType: 'Monthly',
      interval: 3,
      monthDaySelection: [1],
      firstExecutionDate: generateFirstExecutionTimestamp('2024-01-01'),
      terminationType: 'End on Date',
      endDate: dateStringToTimestamp('2024-12-31'),
      cycleStatus: 'Active'
    }
  };

  const config = cycleConfigs[planType];
  return {
    frequencyType: config.frequencyType!,
    interval: config.interval!,
    weekDaySelection: config.weekDaySelection,
    monthDaySelection: config.monthDaySelection,
    firstExecutionDate: config.firstExecutionDate!,
    terminationType: config.terminationType!,
    endDate: config.endDate,
    repeatCount: config.repeatCount,
    cycleStatus: config.cycleStatus!,
    lastGenerationTime: generateMockTimestamp(240), // 10天前生成的时间戳
    nextGenerationTime: generateMockTimestamp(-168) // 7天后的时间戳
  };
};

// Create scope configurations
const createScopeConfig = (planType: PlanType): ScopeConfig => {
  const scopeConfigs: Record<PlanType, Partial<ScopeConfig>> = {
    'Major Plans': {
      scopeType: 'All Stores',
      storeGroupList: [],
      customStoreList: [],
      excludedStores: [],
      storeCount: 1250,
      scopeValidationStatus: 'Validation Passed'
    },
    'Regular Plans': {
      scopeType: 'Specific Store Groups',
      storeGroupList: ['SG001', 'SG002', 'SG003'],
      customStoreList: [],
      excludedStores: ['ST001', 'ST002'],
      storeCount: 850,
      scopeValidationStatus: 'Validation Passed'
    },
    'Minor Plans': {
      scopeType: 'Custom Store Collections',
      storeGroupList: [],
      customStoreList: ['ST001', 'ST002', 'ST003', 'ST004', 'ST005'],
      excludedStores: [],
      storeCount: 5,
      scopeValidationStatus: 'Validation Passed'
    },
    'Urgent Plans': {
      scopeType: 'Individual Stores',
      storeGroupList: [],
      customStoreList: ['ST001', 'ST002'],
      excludedStores: [],
      storeCount: 2,
      scopeValidationStatus: 'Validation Passed'
    },
    'Special Plans': {
      scopeType: 'Specific Store Groups',
      storeGroupList: ['SG001', 'SG004'],
      customStoreList: [],
      excludedStores: ['ST010', 'ST011'],
      storeCount: 450,
      scopeValidationStatus: 'Validation Passed'
    }
  };

  const config = scopeConfigs[planType];
  return {
    scopeType: config.scopeType!,
    storeGroupList: config.storeGroupList!,
    customStoreList: config.customStoreList!,
    storeSelectionCriteria: {
      regions: ['East China', 'South China'],
      storeTypes: ['Standard Store', 'Flagship Store'],
      formats: ['Large', 'Medium']
    },
    excludedStores: config.excludedStores!,
    storeCount: config.storeCount!,
    scopeValidationStatus: config.scopeValidationStatus!,
    lastScopeUpdate: generateMockTimestamp(360) // 15天前更新的时间戳
  };
};

// Create usage statistics
const createUsageStatistics = (planType: PlanType, status: TemplateStatus): UsageStatistics => {
  // If template status is Published, 50% chance activePlans is 0
  const shouldHaveZeroActivePlans = status === 'Published' && Math.random() > 0.5;
  
  const baseStats: Record<PlanType, Partial<UsageStatistics>> = {
    'Major Plans': {
      usageCount: 12,
      activePlans: shouldHaveZeroActivePlans ? 0 : 2,
      lastUsedTime: generateMockTimestamp(120) // 5天前使用的时间戳
    },
    'Regular Plans': {
      usageCount: 48,
      activePlans: shouldHaveZeroActivePlans ? 0 : 8,
      lastUsedTime: generateMockTimestamp(72) // 3天前使用的时间戳
    },
    'Minor Plans': {
      usageCount: 156,
      activePlans: shouldHaveZeroActivePlans ? 0 : 15,
      lastUsedTime: generateMockTimestamp(24) // 1天前使用的时间戳
    },
    'Urgent Plans': {
      usageCount: 23,
      activePlans: shouldHaveZeroActivePlans ? 0 : 3,
      lastUsedTime: generateMockTimestamp(96) // 4天前使用的时间戳
    },
    'Special Plans': {
      usageCount: 8,
      activePlans: shouldHaveZeroActivePlans ? 0 : 1,
      lastUsedTime: generateMockTimestamp(192) // 8天前使用的时间戳
    }
  };

  const stats = baseStats[planType];
  const performanceMetrics: PerformanceMetrics = {
    averageExecutionTime: Math.floor(Math.random() * 20) + 10,
    onTimeCompletionRate: Math.floor(Math.random() * 20) + 80,
    resourceUtilization: Math.floor(Math.random() * 20) + 75,
    errorRate: Math.floor(Math.random() * 10) + 2
  };

  return {
    usageCount: stats.usageCount!,
    activePlans: stats.activePlans!,
    lastUsedTime: stats.lastUsedTime,
    usageFeedback: [
      'Good execution results',
      'Reasonable time arrangement',
      'Proper resource allocation'
    ],
    performanceMetrics
  };
};

// Generate mock templates
export const generateMockTemplates = (count: number = 50): PlanogramTemplate[] => {
  const templates: PlanogramTemplate[] = [];
  const planTypes = PLAN_TYPES.map(pt => pt.value);
  const statuses = TEMPLATE_STATUSES.map(ts => ts.value);
  const creators = ['John Smith', 'Mary Johnson', 'David Wilson', 'Sarah Brown', 'Michael Davis', 'Lisa Miller', 'James Taylor', 'Emma Anderson'];
  const owners = ['Alice Chen', 'Bob Wang', 'Carol Liu', 'Daniel Zhang', 'Eva Li', 'Frank Zhou', 'Grace Wu', 'Henry Xu', 'Iris Yang', 'Jack Ma'];

  for (let i = 1; i <= count; i++) {
    const planType = getRandomItem(planTypes);
    const status = getRandomItem(statuses);
    const businessActivity = getRandomItem(BUSINESS_ACTIVITIES);
    const creator = getRandomItem(creators);
    const owner = getRandomItem(owners);

    const template: PlanogramTemplate = {
      id: generateUniqueId(),
      code: generateTemplateCode(),
      name: `${planType} Template ${i}`,
      planType,
      description: `This is a ${planType} template for ${businessActivity} scenarios. The template includes complete execution processes and milestone configurations.`,
      businessActivity,
      status,
      owner,
      milestones: createMilestoneConfigs(planType),
      executionCycle: createCycleConfig(planType),
      scopeDefinition: createScopeConfig(planType),
      validationStatus: status === 'Published' ? 'Validation Passed' : 
                       status === 'Draft' ? 'Not Validated' : 'Validation Failed',
      usageStatistics: createUsageStatistics(planType, status),
      createTime: getRandomDate(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000), new Date()).getTime(),
      updateTime: getRandomDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), new Date()).getTime(),
      creator,
      remark: Math.random() > 0.7 ? `Remark information ${i}` : undefined
    };

    templates.push(template);
  }

  return templates;
};

// Export mock data
export const MOCK_PLANOGRAM_TEMPLATES = generateMockTemplates(50);

// Export specific templates for testing
export const SAMPLE_TEMPLATES = {
  MAJOR_PLAN: MOCK_PLANOGRAM_TEMPLATES.find(t => t.planType === 'Major Plans')!,
  REGULAR_PLAN: MOCK_PLANOGRAM_TEMPLATES.find(t => t.planType === 'Regular Plans')!,
  MINOR_PLAN: MOCK_PLANOGRAM_TEMPLATES.find(t => t.planType === 'Minor Plans')!,
  URGENT_PLAN: MOCK_PLANOGRAM_TEMPLATES.find(t => t.planType === 'Urgent Plans')!,
  SPECIAL_PLAN: MOCK_PLANOGRAM_TEMPLATES.find(t => t.planType === 'Special Plans')!
};